import React, { useState, useContext, useEffect, useCallback, useRef } from '@wordpress/element';
import { getAddressSuggestions } from '../../api/api';
import { RetailerIdContext } from '../../App';
import { useCartData } from '../../hooks/dutchie-queries';
import { useUpdateCheckout } from '../../hooks/dutchie-mutations';
import { debounce } from '../../util';
import { useSharedAddress } from '../../hooks/useSharedAddress';

const AddressInput = ({ setIsValidAddress }) => {
    const retailerId = useContext(RetailerIdContext);
    const { data: cartData, isFetched: isFetchedCartData } = useCartData(retailerId);
    const handleUpdateAddress = useUpdateCheckout(retailerId);
    const addressInitialized = useRef(false);
    const lastUpdatedAddress = useRef(null);

    const [sharedAddress, setSharedAddress] = useSharedAddress();
    const [suggestions, setSuggestions] = useState([]);
    const [addressDetails, setAddressDetails] = useState({
        street1: '',
        city: '',
        state: '',
        zip: ''
    });

    useEffect(() => {
        if (isFetchedCartData && cartData?.address && !addressInitialized.current) {
            const { street1, city, state, zip } = cartData.address;
            if (city && cartData.address.deliverable) {
                console.log('Initializing from cart data:', cartData.address);
                const fullAddress = `${street1}, ${city}, ${state} ${zip}`;
                setSharedAddress(fullAddress);
                setAddressDetails({ street1, city, state, zip });
                setIsValidAddress(true);
                debouncedUpdateAddress(cartData.address);
                addressInitialized.current = true;
                lastUpdatedAddress.current = fullAddress;
            }
        }
    }, [isFetchedCartData, cartData, setSharedAddress, setIsValidAddress]);

    useEffect(() => {
        if (sharedAddress && !addressInitialized.current && sharedAddress !== lastUpdatedAddress.current) {
            console.log('Initializing from shared address:', sharedAddress);
            const parsedAddress = parseSharedAddress(sharedAddress);
            if (parsedAddress) {
                debouncedUpdateAddress(parsedAddress);
                addressInitialized.current = true;
                lastUpdatedAddress.current = sharedAddress;
            }
        }
    }, [sharedAddress]);

    const parseSharedAddress = (address) => {
        // Implement parsing logic here
        // This is a simple example and might need to be adjusted based on your address format
        const parts = address.split(', ');
        if (parts.length >= 3) {
            return {
                street1: parts[0],
                city: parts[1],
                state: parts[2].split(' ')[0],
                zip: parts[2].split(' ')[1] || ''
            };
        }
        return null;
    };

    const debouncedUpdateAddress = useCallback(
        debounce(async (address) => {
            if (JSON.stringify(address) === JSON.stringify(lastUpdatedAddress.current)) {
                return;
            }
            try {
                const sanitizedAddress = {
                    street1: address.street1,
                    street2: address.street2 || '',
                    city: address.city,
                    state: address.state,
                    zip: address.zip
                };
        
                const result = await handleUpdateAddress.mutateAsync({
                    retailerId,
                    checkoutId: cartData.id,
                    orderType: cartData.orderType,
                    pricingType: cartData.pricingType,
                    address: sanitizedAddress,
                });
                                
                const isValid = result.address.deliverable && result.address.city !== '';
                setIsValidAddress(isValid);
                setAddressDetails(result.address);
                if (isValid) {
                    const fullAddress = `${result.address.street1}, ${result.address.city}, ${result.address.state} ${result.address.zip}`;
                    setSharedAddress(fullAddress);
                    lastUpdatedAddress.current = fullAddress;
                }
            } catch (error) {
                console.error('Failed to update address:', error);
                setIsValidAddress(false);
            }
        }, 300),
        [cartData, handleUpdateAddress.mutateAsync, retailerId, setIsValidAddress, setSharedAddress]
    );

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSharedAddress(value);
        addressInitialized.current = false;

        if (value === '') {
            debouncedUpdateAddress({
                street1: '',
                city: '',
                state: '',
                zip: '',
            });
            setSuggestions([]);
            setIsValidAddress(false);
        } else {
            debouncedHandleInputChange(value);
        }
    };
    
    const debouncedHandleInputChange = useCallback(
        debounce(async (value) => {
            if (value !== '') {
                const response = await getAddressSuggestions(encodeURIComponent(value));
                if (response?.results) {
                    setSuggestions(response.results.map((result) => result.address));
                }
            } else {
                setSuggestions([]);
            }
        }, 300),
        []
    );

    return (
        <div className='headless-cart-delivery-inputs'>
            {!sharedAddress ? (
                <div className='headless-cart-delivery-msg'>
                    Please enter your delivery address
                </div>
            ) : cartData?.address && cartData.address.city && !cartData.address.deliverable ? (
                <div className='headless-cart-delivery-msg'>
                    Address is outside delivery range!
                </div>
            ) : null}
            <input
                type='text'
                onChange={handleInputChange}
                value={sharedAddress}
            />

            <ul
                className='headless-delivery-autofill'
                style={{ display: suggestions.length > 0 ? 'block' : 'none' }}
            >
                {isFetchedCartData
                    ? suggestions.map((suggestion, i) => (
                          <li
                              key={i}
                              onClick={() => {
                                  const newAddress = {
                                      street1: `${suggestion.streetNumber} ${suggestion.streetName}`,
                                      city: suggestion.municipality,
                                      state: suggestion.countrySubdivision,
                                      zip: suggestion.postalCode,
                                  };
                                  setSharedAddress(suggestion.freeformAddress);
                                  setSuggestions([]);
                                  debouncedUpdateAddress(newAddress);
                              }}
                          >
                              {suggestion.freeformAddress}
                          </li>
                      ))
                    : null}
            </ul>
        </div>
    );
};

export default AddressInput;
